import { Direction } from '@angular/cdk/bidi';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable, Signal } from '@angular/core';
import { FilterKeywords } from '@common/components/filter/filter-keywords';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { Path } from '@common/data-models/complex-types/path';
import { PSTFieldNames, PSTFieldNamesEnum } from '@common/data-models/fields/pst-field-names';
import { CustomFilterDef } from '@common/data-models/navigation/custom-filter-def';
import { FilterDef } from '@common/data-models/navigation/filter-def';
import { ViewStateOptions } from '@common/data-models/navigation/viewstate-options';
import { Record } from '@common/data-models/records/record';
import { ResultGroupStaticServices } from '@common/data-models/search-results/result-group';
import { GroupCostPermissions, GroupPermissions } from '@common/data-models/service/group-permissions';
import { GroupSecurityPrivs } from '@common/data-models/service/group-security-privs';
import { ListDataMetaData } from '@common/data-models/service/list-data';
import { MosDevice } from '@common/data-models/service/mos-device';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { Staff } from '@common/data-models/service/staff';
import { StorytellingNavigation } from '@common/data-models/service/storytelling-navigation';
import { OPTION_ALL, OPTION_BLANK, OPTION_NO, OPTION_YES, PSTWEB_BUTTON_HT, PSTWEB_LIST_VALUE, ST_CRITERIA_SECURITY_ROLE } from '@common/enums/constants';
import { DataType } from '@common/enums/data-type';
import { ObjectTypes } from '@common/enums/object-types';
import { IGlobalTablesService } from '@common/interfaces/i-global-tables-service';
import { Util } from '@common/utils/util';
import { Store } from '@ngrx/store';
import { AppTheme } from '@src/suite/app.theme';
import { CriteriaObj } from '@src/suite/common/data-models/criteria/criteria-obj';
import { GlobalTablesActions } from '@src/suite/common/store/global-tables/global-tables.actions';
import { HelloResultsActions } from '@src/suite/common/store/hello-results/hello-results.actions';
import { i18nActions } from '@src/suite/common/store/i18n/actions/i18n.actions';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { DiscoverProviderResponse, DiscoverWireCategoryResponse } from '@src/suite/discover/store/models/feed.provider.models';
import { FieldDefValidations } from '@suite/common/enums/enums';
import moment from 'moment-timezone';
import { ReplaySubject, Subject } from 'rxjs';
import XRegExp from 'xregexp';

import { AsyncServiceBase } from './async-service-base';
import { Convertor } from './convertor';
import { ExternalActionsService } from './external-actions-service';
import { LanguageSettings } from './language-settings';
import { LogonService } from './logon-service';
import { NomWebServicesJson } from './nom-web-services-json';
import { VisualStateService } from './visual-state-service';

type Criteria = {
    matches: RegExpMatchArray;
    criteria: string;
    buffer: { [id: string]: RegExpMatchArray };
};

@Injectable({ providedIn: 'root' })
export class GlobalTablesService extends AsyncServiceBase implements IGlobalTablesService {
    public nomWebServicesJson: NomWebServicesJson = inject(NomWebServicesJson);
    public logonService: LogonService = inject(LogonService);
    public visualStateService: VisualStateService = inject(VisualStateService);
    public languageSettings: LanguageSettings = inject(LanguageSettings);
    public externalActionsService: ExternalActionsService = inject(ExternalActionsService);
    public convertor: Convertor = inject(Convertor);
    private store: Store = inject(Store);
    constructor() {
        super();
        this.convertor.globalTablesService = this;
        FilterKeywords.globalTablesService = this;
        ResultGroupStaticServices.globalTablesService = this;
        Record.globalTablesService = this;
        AppTheme.globalTablesService = this;
        Path.globalTablesService = this;

        this.logonService.hasActiveSession.subscribe(() => {
            this.getGlobalTablesAndResolve();
        });
    }

    public globalTablesLoaded = new ReplaySubject<void>(1);

    public async getGlobalTablesAndResolve(): Promise<any> {
        await this.getGlobalTablesSimple();
        this.globalTablesLoaded.next();
        this.store.dispatch(
            GlobalTablesActions.setGlobalTables({
                Security: this.Security,
                Adorners: this.Adorners,
                Apps: this.Apps,
                Groups: this.Groups,
                GroupsArray: this.GroupsArray,
                EnterpriseGroup: this.EnterpriseGroup,
                PlaybookTeamsArray: this.PlaybookTeamsArray,
                DefaultPlaybookTeam: this.DefaultPlaybookTeam,
                EnterprisePlaybookGroup: this.EnterprisePlaybookGroup,
                FieldMappings: this.FieldMappings,
                MOSDevices: this.MOSDevices,
                Privileges: this.Privileges,
                Servers: this.Servers,
                WireCategories: this.WireCategories,
                WireProviders: this.WireProviders,
                Languages: this.Languages,
                PersonalLanguage: this.PersonalLanguage,
                CommonTimeZones: this.CommonTimeZones,
                IanaTimeZones: this.IanaTimeZones,
                FilterDefs: this.FilterDefs,
            })
        );
        this.initializeResolve(null);
    }

    private calculateListItemTitle(propertyId: string, id: string, title: string): string {
        if (this.languageSettings.get(`${PSTWEB_LIST_VALUE}_${propertyId}_${id}`).includes(PSTWEB_LIST_VALUE)) {
            return title;
        }
        return this.languageSettings.get(`${PSTWEB_LIST_VALUE}_${propertyId}_${id}`);
    }

    private clearTitleCache() {
        this.listItemsCache = {};
        this.listItemsForTreeCache = {};
        this.listItemsForFieldCache = {};
        this.listItemsForDeskCache = [];
        this.listColumnsForSpecialFieldCache = {};
    }

    private calculateInitials(name: string): string {
        if (!name) {
            return '';
        }
        let initials = '';
        const words = name.split(' ');
        if (words.length > 1) {
            initials = `${words[0]?.charAt(0)?.toUpperCase()}${words[words.length - 1].charAt(0)?.toUpperCase()}`;
            return initials;
        }
        initials = `${words[0].charAt(0)?.toUpperCase()}${words[0].charAt(1)}`;

        return initials;
    }

    private calculateEditableForDesk(id: string, type: ObjectTypes) {
        switch (type) {
            case ObjectTypes.TOPIC:
                return this.securityPrivileges[id].topic.write;
            case ObjectTypes.SCRIPT:
                return this.securityPrivileges[id].story.write;
            case ObjectTypes.ASSIGNMENT:
                return this.securityPrivileges[id].assignment.write;
            case ObjectTypes.PSTVIEWSTATE:
                return true;
        }
        return false;
    }

    private fieldDefsLoadedSource = new Subject<boolean>();
    public FieldDefsLoadedObservable = this.fieldDefsLoadedSource.asObservable();

    public ImplementedValidationTypes: Array<string> = [
        'CHECKLIST',
        'COMBOBOX',
        'TEXT',
        'CHECKBOX',
        'TIME',
        'DATE',
        'MOSDATETIME',
        'STAFFIDLIST',
        'COST',
        'SPECIAL',
        'TREE',
        'LOOKUPLIST',
        'ADDRESS',
        'DATETIMERANGE',
    ];

    public PropertiesGroupKey: string = 'PROPERTIES';

    public Security: Array<any> = [];
    public Apps: Array<any> = [];
    public Groups: { [id: string]: Group } = {};
    public GroupsArray: Array<Group> = [];
    public AllGroups: { [id: string]: Group } = {};
    public AllGroupsArray: Array<Group> = [];
    public EnterpriseGroup: Group;
    public PlaybookTeamsArray: Array<Group> = [];
    public DefaultPlaybookTeam: Group = null;
    public EnterprisePlaybookGroup: string = null;
    public HomeServerGroups: Array<string> = [];

    public FieldMappings: Array<FieldMapping> = [];

    public AppVersion: string = '';
    public PlaybookHelpUrl: string;
    public setPlaybookHelpUrl() {
        if (this.visualStateService.ConfigSettings.PlaybookHelpUrl) {
            this.PlaybookHelpUrl = this.visualStateService.ConfigSettings.PlaybookHelpUrl;
            if (this.PlaybookHelpUrl.indexOf('$version$') > -1) {
                this.PlaybookHelpUrl = this.PlaybookHelpUrl.replace('$version$', this.AppVersion.replace(/\./g, ''));
            }
        }
    }

    public MOSDevices: Array<MosDevice> = [];
    public Privileges: Array<any> = [];
    public Servers: { [id: string]: Server } = {};
    public Adorners: { [id: string]: Adorner } = {};

    public adornersIDS = {
        TopicCardLeftBorder: 'TopicCardLeftBorder',
        AssignmentIcon: 'AssignmentIcon',
        StoryIcon: 'StoryIcon',
        AssignmentCardLeftBorder: 'AssignmentCardLeftBorder',
        TopicCardTypeColor: 'TopicCardTypeColor',
        TopicIcon: 'TopicIcon',
        StoryCardLeftBorder: 'StoryCardLeftBorder',
    };

    public WireCategories: Array<DiscoverWireCategoryResponse> = [];
    public WireProviders: Array<DiscoverProviderResponse> = [];

    public FieldDefs: Array<FieldDef> = [];
    public FieldDefsDict: { [id: string]: FieldDef } = {};

    public StorytellingNavigation: Array<StorytellingNavigation> = [];
    public StorytellingNavigationDict: { [product: string]: StorytellingNavigation } = {};

    public CustomFilterDef: Array<CustomFilterDef> = [];
    public CustomFilterDefsDict: { [id: string]: CustomFilterDef } = {};

    public Languages: Array<ENPSLanguage> = [];

    public PersonalLanguage: ENPSLanguage = null;

    public CommonTimeZones: Array<TimeZone> = [];
    public IanaTimeZones: Array<TimeZone> = [];
    public PersonalTimeZone: TimeZone = null;
    public PersonalTimeZoneDisplay: TimeZone = null;
    public ClientTimeZone: TimeZone = null;
    public DefaultTimeZone: TimeZone = null;

    public FilterDefs: { [id: number]: Array<FilterDef> } = {};
    public ViewStateOptionsDefs: { [type: number]: { [field: string]: ViewStateOptions } } = {};

    public getGroupingOptions: { [id: number]: () => ViewStateOptions } = {};
    public getSortingOptions: { [id: number]: () => ViewStateOptions } = {};
    public getLayoutOptions: { [id: number]: () => ViewStateOptions } = {};
    public calculateDefaultGroupSort: { [id: string]: () => void } = {};
    public clearServices: { [id: string]: () => void } = {};
    public getOptions(type: number, field: string): ViewStateOptions {
        if (this.ViewStateOptionsDefs[type] && this.ViewStateOptionsDefs[type][field]) {
            return this.ViewStateOptionsDefs[type][field];
        }
        let options: ViewStateOptions = new ViewStateOptions();

        if (field === PSTFieldNames.PSTViewStateGrouping && this.getGroupingOptions[type]) {
            options = this.getGroupingOptions[type]();
        }
        if (field === PSTFieldNames.PSTViewStateSorting && this.getSortingOptions[type]) {
            options = this.getSortingOptions[type]();
        }
        if (field === PSTFieldNames.PSTViewStateLayout && this.getLayoutOptions[type]) {
            options = this.getLayoutOptions[type]();
        }

        if (!this.ViewStateOptionsDefs[type]) this.ViewStateOptionsDefs[type] = {};
        this.ViewStateOptionsDefs[type][field] = options;
        return this.ViewStateOptionsDefs[type][field];
    }

    public get DefaultCheckListValue(): MultiValue<string> {
        return new MultiValue<string>();
    }

    public propertiesGroupCache: { [id: string]: string } = {};
    public staticPropertyHandlers: { [id: string]: () => { [id: string]: boolean } } = {};
    private dynamicPropertiesCache: { [id: string]: Array<FieldDef> } = {};
    public getDynamicPropertiesFor(type: string, group: string): Array<FieldDef> {
        const cacheKey = `${type}_${group}`;
        if (this.dynamicPropertiesCache[cacheKey]) return this.dynamicPropertiesCache[cacheKey];
        let excluded: { [id: string]: boolean } = {};
        if (this.staticPropertyHandlers[type.toUpperCase()]) {
            excluded = this.staticPropertyHandlers[type.toUpperCase()]();
        }
        if (this.FieldDefs.length > 0) {
            const ret = this.FieldDefs.filter(item => {
                if (item.ID === 'PSTStoryTopicGUID') return false;
                // TODO: add 'PSTStoryTopicTitle'
                if (excluded && excluded[item.ID]) return false;
                if (item.Validation.toUpperCase() === 'SPECIAL') return false;
                if (item.Validation.toUpperCase() === 'TABLE') {
                    return false;
                }
                if (item.Validation.toUpperCase() === 'COST') {
                    this.propertiesGroupCache[item.ID] = 'Cost';
                    return false;
                }
                if (type === 'PublicationChannel') {
                    return item.PublicationChannelProp === true;
                }

                const trimmedGroup = item?.FieldGroup?.trim();
                if (type === 'Script') {
                    return trimmedGroup === group && item.Type && (item.Type.indexOf('Script') >= 0 || item.Type.indexOf('RO Column') >= 0 || item.Type.indexOf('RO+NG Column') >= 0 || item.StoryProp);
                }
                try {
                    return item[`${type}Prop`] === true && (trimmedGroup === group || (group === '' && trimmedGroup?.toUpperCase() === this.PropertiesGroupKey));
                } catch (e) {
                    console.log(e);
                    return false;
                }
            });
            ret.sort((a: FieldDef, b: FieldDef) => a.Sequence - b.Sequence);
            this.dynamicPropertiesCache[cacheKey] = ret;

            for (const prop of ret) {
                this.propertiesGroupCache[prop.ID] = group;
            }

            return this.dynamicPropertiesCache[cacheKey];
        }
        return [];
    }

    public isGroupSingleValue: boolean = false;
    public setIsGroupSingleValue() {
        if (this.visualStateService.ConfigSettings.EnablePlaybookSingleGroupSelection === '1') {
            this.isGroupSingleValue = true;
        } else {
            this.isGroupSingleValue = false;
        }
    }

    public EnforceRequiredFieldsOnCreate: boolean = false;
    public setEnforceRequiredFieldsOnCreate() {
        if (this.visualStateService.ConfigSettings.EnforceRequiredFieldsOnCreate) {
            this.EnforceRequiredFieldsOnCreate = !!parseInt(this.visualStateService.ConfigSettings.EnforceRequiredFieldsOnCreate);
        }
    }

    private allPropertyGroupsCache: { [id: string]: Array<{ name: string; dynamic: boolean }> } = {};

    public getAllPropertyGroupsFor(type: string): Array<{ name: string; dynamic: boolean }> {
        if (this.allPropertyGroupsCache[type]) return this.allPropertyGroupsCache[type];
        const allPropertyGroups: Array<{ name: string; dynamic: boolean }> = [];
        // field def groups
        const fieldGroups: Array<string> = this.getFieldGroupsFor(type);
        for (const group of fieldGroups) {
            allPropertyGroups.push({ name: group, dynamic: true });
        }
        // add Assignments and Attachments
        allPropertyGroups.push({ name: this.languageSettings.PSTWebCoverageAssignmentsPane, dynamic: false });
        allPropertyGroups.push({ name: this.languageSettings.PSTWebStoriesPane, dynamic: false });
        allPropertyGroups.push({ name: this.languageSettings.PSTWebAttachmentsPane, dynamic: false });

        if (Util.directoryApp(this.StorytellingNavigation)) {
            allPropertyGroups.push({ name: this.languageSettings.PSTWebContactsPane, dynamic: false });
        }

        // specials
        let specials: Array<FieldDef> = [];
        if (type.toUpperCase() === 'TOPIC') {
            specials = this.getSpecialPropertiesFor('Topic');
        }
        if (type.toUpperCase() === 'ASSIGNMENT') {
            specials = this.getSpecialPropertiesFor('Assignment');
        }

        if (type.toUpperCase() === 'SCRIPT') {
            specials = this.getSpecialPropertiesFor('Script');
        }

        for (const spec of specials) {
            allPropertyGroups.push({ name: spec.DefaultCaption, dynamic: false });
        }

        if (this.visualStateService.ConfigSettings.FieldGroupsOrder) {
            const allSortedFieldGroups: Array<string> = this.visualStateService.ConfigSettings.FieldGroupsOrder.split('|');
            const currentType = allSortedFieldGroups.find(field => field.toUpperCase().startsWith(type.toUpperCase()));
            if (currentType) {
                const fGroups = currentType.split('-')[1];
                if (fGroups?.length > 0) {
                    const sortedFieldGroups = fGroups?.split(',');
                    if (sortedFieldGroups && sortedFieldGroups?.length > 0) {
                        if (sortedFieldGroups.indexOf('Properties') > -1) sortedFieldGroups.splice(sortedFieldGroups.indexOf('Properties'), 1, '');
                        allPropertyGroups.sort((a, b) => {
                            const indexA = sortedFieldGroups.indexOf(a.name) >= 0 ? sortedFieldGroups.indexOf(a.name) : 1000;
                            const indexB = sortedFieldGroups.indexOf(b.name) >= 0 ? sortedFieldGroups.indexOf(b.name) : 1000;
                            return indexA - indexB;
                        });
                    }
                }
            }
        }
        this.allPropertyGroupsCache[type] = allPropertyGroups;
        return this.allPropertyGroupsCache[type];
    }

    private fieldGroupsCache: { [id: string]: Array<string> } = {};
    public getFieldGroupsFor(type: string): Array<string> {
        if (this.fieldGroupsCache[type]) return this.fieldGroupsCache[type];
        if (this.FieldDefs.length > 0) {
            const map: { [id: string]: boolean } = {};
            const fields = this.FieldDefs.filter(item => {
                if (item.Validation.toUpperCase() === 'TABLE') {
                    return false;
                }
                return true;
            });
            for (const field of fields) {
                if (type === 'Script') {
                    if (field.Type && (field.Type.indexOf('Script') >= 0 || field.Type.indexOf('RO Column') >= 0 || field.Type.indexOf('RO+NG Column') >= 0 || field.StoryProp)) {
                        map[field.FieldGroup] = true;
                    }
                } else if (field[`${type}Prop`] === true && !(field.Validation === 'Cost' || field.Validation === 'Special')) map[field.FieldGroup] = true;
            }
            const ret: Array<string> = [''];
            for (const key in map) {
                if (key.trim() !== '' && key.trim().toUpperCase() !== this.PropertiesGroupKey) {
                    ret.push(key.trim());
                }
            }
            this.fieldGroupsCache[type] = ret;
        } else {
            return [];
        }

        return this.fieldGroupsCache[type];
    }

    public getExternalFilesFieldDef(): FieldDef {
        return this.FieldDefs.filter(item => item.ID === 'ExternalFiles')[0];
    }

    private specialPropertiesCache: { [id: string]: Array<FieldDef> } = {};
    public getSpecialPropertiesFor(type: string): Array<FieldDef> {
        if (this.specialPropertiesCache[type]) return this.specialPropertiesCache[type];
        if (this.FieldDefs.length > 0) {
            let ret = [];
            if (type === 'Script') {
                ret = this.FieldDefs.filter(item => (item.Validation.toUpperCase() === 'SPECIAL' || item.Validation.toUpperCase() === 'COST') && item.Type.indexOf(type) >= 0);
            } else {
                ret = this.FieldDefs.filter(item => (item.Validation.toUpperCase() === 'SPECIAL' || item.Validation.toUpperCase() === 'COST') && item[`${type}Prop`] === true);
            }
            ret.sort((a: FieldDef, b: FieldDef) => a.Sequence - b.Sequence);
            this.specialPropertiesCache[type] = ret;
            return this.specialPropertiesCache[type];
        }
        return [];
    }

    private costPropertiesCache: { [id: string]: Array<FieldDef> } = {};
    public getCostPropertiesFor(type: string): Array<FieldDef> {
        if (this.costPropertiesCache[type]) return this.costPropertiesCache[type];
        if (this.FieldDefs.length > 0) {
            let ret = [];
            if (type === 'Script') {
                ret = this.FieldDefs.filter(item => item.Validation.toUpperCase() === 'COST' && item.Type.indexOf(type) >= 0);
            } else {
                ret = this.FieldDefs.filter(item => item.Validation.toUpperCase() === 'COST' && item[`${type}Prop`] === true);
            }
            this.costPropertiesCache[type] = ret;
            return this.costPropertiesCache[type];
        }
        return [];
    }

    private addressPropertiesCache: { [id: string]: Array<FieldDef> } = {};
    public getAddressPropertiesFor(type: string): Array<FieldDef> {
        if (this.addressPropertiesCache[type]) return this.addressPropertiesCache[type];
        if (this.FieldDefs.length > 0) {
            let ret = [];
            if (type === 'Script') {
                ret = this.FieldDefs.filter(item => item.Validation.toUpperCase() === 'ADDRESS' && item.Type.indexOf(type) >= 0);
            } else {
                ret = this.FieldDefs.filter(item => item.Validation.toUpperCase() === 'ADDRESS' && item[`${type}Prop`] === true);
            }
            ret.sort((a: FieldDef, b: FieldDef) => a.Sequence - b.Sequence);
            this.addressPropertiesCache[type] = ret;
            return this.addressPropertiesCache[type];
        }
        return [];
    }

    public isDateProperty(id: string): boolean {
        const prop = this.FieldDefsDict[id] || ListDataMetaData[id];
        if (prop && ((prop.Validation && ['TIME', 'DATE', 'MOSDATETIME'].indexOf(prop.Validation.toUpperCase()) > -1) || prop === DataType.DATE)) {
            return true;
        }
        return false;
    }

    private rolePropertiesCache: { [id: string]: Array<FieldDef> } = {};
    public getRolePropertiesFor(type: string): Array<FieldDef> {
        if (this.rolePropertiesCache[type]) return this.rolePropertiesCache[type];
        if (this.FieldDefs.length > 0) {
            let ret = [];
            if (type === 'Script') {
                ret = this.FieldDefs.filter(item => item.Validation.toUpperCase() === 'STAFFIDLIST' && item.Type.indexOf(type) >= 0);
            } else {
                ret = this.FieldDefs.filter(item => item.Validation.toUpperCase() === 'STAFFIDLIST' && item[`${type}Prop`] === true);
            }

            ret.sort((a: FieldDef, b: FieldDef) => a.Sequence - b.Sequence);
            this.rolePropertiesCache[type] = ret;
            return this.rolePropertiesCache[type];
        }
        return [];
    }

    public getFiltersForViewStateType: { [id: number]: () => Array<FilterDef> } = {};

    private supportedFilterValidations: Array<FieldDefValidations> = [
        FieldDefValidations.TEXT,
        FieldDefValidations.PSTPUBLICATIONLISTTIME,
        FieldDefValidations.TIME,
        FieldDefValidations.DATE,
        FieldDefValidations.MOSDATETIME,
        FieldDefValidations.DATETIMERANGE,
        FieldDefValidations.CHECKLIST,
        FieldDefValidations.SELECT,
        FieldDefValidations.COMBOBOX,
        FieldDefValidations.CHECKBOX,
        FieldDefValidations.STAFFIDLIST,
        FieldDefValidations.LOOKUPLIST,
        FieldDefValidations.TREE,
    ];
    private supportedFilterValidationsSet: Set<FieldDefValidations> = new Set(this.supportedFilterValidations);

    public getFilters(type: number): Array<FilterDef> {
        if (this.FilterDefs[type]) {
            return this.FilterDefs[type];
        }
        let filterDefinitions: Array<FilterDef> = [];
        let fieldDefinitions: Array<FieldDef> = [];

        if (this.getFiltersForViewStateType[type]) {
            fieldDefinitions = this.FieldDefs.filter(item => this.supportedFilterValidationsSet.has(item.Validation.toUpperCase() as FieldDefValidations)).filter(
                this.getFiltersForViewStateType[type]
            );
        }
        filterDefinitions = fieldDefinitions
            .sort((a: FieldDef, b: FieldDef) => {
                if (a.Sequence > b.Sequence) return 1;
                if (a.Sequence < b.Sequence) return -1;
                return 0;
            })
            .map(item => {
                const filter: FilterDef = new FilterDef(item, null);

                if (filter.Validation === FieldDefValidations.CHECKLIST || filter.Validation === FieldDefValidations.COMBOBOX || filter.Validation === FieldDefValidations.SELECT) {
                    if (filter.ID === PSTFieldNames.PSTDesk) {
                        filter.Options = [
                            { id: OPTION_ALL, title: this.languageSettings.PSTWebAllOption, selectable: true },
                            { id: OPTION_BLANK, title: this.languageSettings.PSTWebBlankOption, selectable: true },
                        ].concat(this.getListItemsForDesk(ObjectTypes.PSTVIEWSTATE));
                    } else if (filter.ID === PSTFieldNames.PSTWireProvider) {
                        filter.Options = [{ id: OPTION_ALL, title: this.languageSettings.PSTWebAllOption, selectable: true }].concat(this.getListItemsForWireProvider());
                    } else if (filter.ID === PSTFieldNames.PSTWireCategory) {
                        filter.Options = [{ id: OPTION_ALL, title: this.languageSettings.PSTWebAllOption, selectable: true }].concat(this.getListItemsForWireCategory());
                    } else if (filter.ID === PSTFieldNames.PSTTeamLocation) {
                        const teamsArr = this.getListItemsForTeamLocations();
                        if (teamsArr.length > 0) {
                            filter.Options = [
                                { id: OPTION_ALL, title: this.languageSettings.PSTWebAllOption, selectable: true },
                                { id: OPTION_BLANK, title: this.languageSettings.PSTWebBlankOption, selectable: true },
                            ].concat(teamsArr);
                        } else {
                            return null;
                        }
                    } else {
                        filter.Options = [
                            { id: OPTION_ALL, title: this.languageSettings.PSTWebAllOption, selectable: true },
                            { id: OPTION_BLANK, title: this.languageSettings.PSTWebBlankOption, selectable: true },
                        ].concat(this.getListItems(filter.ID, item.List));
                    }
                }
                if (filter.Validation === FieldDefValidations.CHECKBOX) {
                    filter.Options = [
                        { id: OPTION_ALL, title: this.languageSettings.PSTWebAllOption },
                        { id: OPTION_BLANK, title: this.languageSettings.PSTWebBlankOption },
                        { id: OPTION_YES, title: this.languageSettings.PSTWebYesOption },
                        { id: OPTION_NO, title: this.languageSettings.PSTWebNoOption },
                    ];
                }
                if (filter.Validation === FieldDefValidations.TREE) {
                    if (filter.ID === PSTFieldNames.PSTServerLocations) {
                        filter.Options = [{ id: OPTION_ALL, value: this.languageSettings.PSTWebAllOption, children: [] }];
                        for (const id in this.Servers) {
                            const children: Array<{ id: string; value: string }> = [];
                            for (const gid in this.AllGroups) {
                                if (this.AllGroups[gid].Server === this.Servers[id].ServerID) {
                                    children.push({ id: this.AllGroups[gid].ID, value: this.AllGroups[gid].Description });
                                }
                            }
                            filter.Options.push({ id: id, value: this.Servers[id].Description, children: children });
                        }
                    } else {
                        filter.Options = [
                            { id: OPTION_ALL, value: this.languageSettings.PSTWebAllOption, children: [] },
                            { id: OPTION_BLANK, value: this.languageSettings.PSTWebBlankOption, children: [] },
                        ].concat(this.getlistItemsForTree(item.List).map(f => ({ id: f.id, value: f.value, children: f.children })));
                    }
                }
                return filter;
            })
            .filter(f => f);
        this.FilterDefs[type] = filterDefinitions;
        return this.FilterDefs[type];
    }

    private listItemsCache: { [id: string]: Array<{ id: string; title: string; selectable: boolean }> } = {};
    public getListItems(fieldID: string, listString: string): Array<{ id: string; title: string; selectable: boolean }> {
        if (this.listItemsCache[listString]) return this.listItemsCache[listString];
        this.listItemsCache[listString] = [];
        const chunks = listString ? listString.split('|') : [];
        for (const c of chunks) {
            this.listItemsCache[listString].push({ id: c, title: this.calculateListItemTitle(fieldID, c, c), selectable: true });
        }
        this.listItemsCache[listString];
        return this.listItemsCache[listString];
    }

    private listItemsForTreeCache: { [id: string]: Array<any> } = {};
    public getlistItemsForTree(listString: string): Array<any> {
        if (this.listItemsForTreeCache[listString]) return this.listItemsForTreeCache[listString];
        this.listItemsForTreeCache[listString] = [];
        const chunks = listString ? listString.split('|') : [];
        for (const c in chunks) {
            for (const field of this.FieldDefs) {
                if (field.Validation === 'Tree') {
                    const nodeChunks = chunks[c].split('\\');
                    // const nodeChunkTranslation = this.languageSettings.get(`${PSTWEB_LIST_VALUE}_${field.ID}_${nodeChunks[0]}`).includes(PSTWEB_LIST_VALUE) ? nodeChunks[0] : this.languageSettings.get(`${PSTWEB_LIST_VALUE}_${field.ID}_${nodeChunks[0]}`);
                    const nodeChunkTranslation = this.calculateListItemTitle(field.ID, nodeChunks[0], nodeChunks[0]);
                    const root = {
                        value: nodeChunkTranslation,
                        id: nodeChunks[0],
                        children: [],
                    };
                    this.populateChildren(root, nodeChunks.splice(1, nodeChunks.length - 1), field.ID);
                    this.listItemsForTreeCache[listString].push(root);
                }
            }
        }
        this.listItemsForTreeCache[listString];
        return this.listItemsForTreeCache[listString];
    }

    public populateChildren(root, nodeChunks, fieldId) {
        let currentNode = root;
        while (nodeChunks.length > 0) {
            const chunk = nodeChunks.shift();
            // const chunkTranslation = this.languageSettings.get(`${PSTWEB_LIST_VALUE}_${fieldId}_${chunk}`).includes(PSTWEB_LIST_VALUE) ? chunk : this.languageSettings.get(`${PSTWEB_LIST_VALUE}_${fieldId}_${chunk}`);
            const chunkTranslation = this.calculateListItemTitle(fieldId, chunk, chunk);
            if (chunk === '+') {
                this.populateChildren(currentNode, nodeChunks, fieldId);
            } else if (chunk === '-') {
                return;
            } else {
                if (!root.children) root.children = [];
                root.children.push({ value: chunkTranslation, id: chunk, parentId: root.id });
                currentNode = root.children[root.children.length - 1];
            }
        }
    }

    private listItemsForFieldCache: { [id: string]: Array<{ id: string; title: string }> } = {};
    public getListItemsForField(field: string): Array<{ id: string; title: string }> {
        if (this.listItemsForFieldCache[field]) return this.listItemsForFieldCache[field];

        if (this.FieldDefs.length > 0) {
            for (const f of this.FieldDefs) {
                if (f.ID === field) {
                    const chunks = f.List ? f.List.split('|') : [];
                    this.listItemsForFieldCache[field] = [];
                    for (const c of chunks) {
                        this.listItemsForFieldCache[field].push({ id: c, title: this.calculateListItemTitle(field, c, c) });
                    }
                    break;
                }
            }
            this.listItemsForFieldCache[field];
            return this.listItemsForFieldCache[field];
        }
        return [];
    }

    private defaultCaptionForFieldCache: { [id: string]: string } = {};
    public defaultCaptionForField(fieldId: string): string {
        if (this.defaultCaptionForFieldCache[fieldId]) return this.defaultCaptionForFieldCache[fieldId];

        if (this.FieldDefs.length > 0) {
            for (const f of this.FieldDefs) {
                if (f.ID === fieldId) {
                    this.defaultCaptionForFieldCache[fieldId] = f.DefaultCaption;
                    break;
                }
            }
            return this.defaultCaptionForFieldCache[fieldId];
        }
        return '';
    }

    private fieldDefCache: { [id: string]: FieldDef } = {};
    public getFieldDef(fieldId: string): FieldDef {
        if (this.fieldDefCache[fieldId]) return this.fieldDefCache[fieldId];
        for (const fieldDef of this.FieldDefs) {
            if (fieldDef.ID.toUpperCase() === fieldId.toUpperCase()) {
                this.fieldDefCache[fieldId] = fieldDef;
                break;
            }
        }
        return this.fieldDefCache[fieldId];
    }

    public isScriptProperty(fieldDef: FieldDef): boolean {
        return fieldDef.Type?.indexOf('Script') > -1 || fieldDef.StoryProp || false;
    }

    public isObjectTypeFilter(type: ObjectTypes, id: string): boolean {
        const fieldDef = this.getFieldDef(id);

        if (!fieldDef.Briefable) return false;

        let t = '';
        if (type === ObjectTypes.TOPIC) {
            t = 'Topic';
        }
        if (type === ObjectTypes.ASSIGNMENT) {
            t = 'Assignment';
        }
        if (type === ObjectTypes.SCRIPT) {
            t = 'Script';
        }

        if ((type === ObjectTypes.SCRIPT && this.isScriptProperty(fieldDef)) || (t && fieldDef[`${t}Prop`] === true)) {
            return true;
        }

        if (type === ObjectTypes.RUNDOWN && (fieldDef.RundownProp === true || fieldDef.Type === 'RO Property')) {
            return true;
        }

        return false;
    }

    private isSpecialFieldTypeCache: { [id: string]: boolean } = {};
    public isSpecialFieldType(fieldId: string): boolean {
        if (!(this.isSpecialFieldTypeCache[fieldId] === undefined)) return this.isSpecialFieldTypeCache[fieldId];
        for (const f of this.FieldDefs) {
            if (f.ID === fieldId && (f.Validation.toUpperCase() === FieldDefValidations.SPECIAL || f.Validation.toUpperCase() === FieldDefValidations.COST)) {
                this.isSpecialFieldTypeCache[fieldId] = true;
                return true;
            }
        }
        this.isSpecialFieldTypeCache[fieldId] = false;
        return false;
    }

    private isCostFieldTypeCache: { [id: string]: boolean } = {};
    public isCostFieldType(fieldId: string): boolean {
        if (!(this.isCostFieldTypeCache[fieldId] === undefined)) return this.isCostFieldTypeCache[fieldId];
        for (const f of this.FieldDefs) {
            if (f.ID === fieldId && f.Validation.toUpperCase() === FieldDefValidations.COST) {
                this.isCostFieldTypeCache[fieldId] = true;
                return true;
            }
        }
        this.isCostFieldTypeCache[fieldId] = false;
        return false;
    }

    private customItemsFieldType = {
        PSTViewStateTopicCardCustomItems: true,
    };
    public isCustomItemFieldType(fieldId: string): boolean {
        return this.customItemsFieldType[fieldId];
    }

    private isLookupListFieldTypeCache: { [id: string]: boolean } = {};
    public isLookupListFieldType(fieldId: string): boolean {
        if (!(this.isLookupListFieldTypeCache[fieldId] === undefined)) return this.isLookupListFieldTypeCache[fieldId];
        for (const f of this.FieldDefs) {
            if (f.ID === fieldId && f.Validation.toUpperCase() === FieldDefValidations.LOOKUPLIST) {
                this.isLookupListFieldTypeCache[fieldId] = true;
                return true;
            }
        }
        this.isLookupListFieldTypeCache[fieldId] = false;
        return false;
    }

    private isDateRangeFieldTypeCache: { [id: string]: boolean } = {};
    public isDateRangeFieldType(fieldId: string): boolean {
        if (!(this.isDateRangeFieldTypeCache[fieldId] === undefined)) return this.isDateRangeFieldTypeCache[fieldId];
        for (const f of this.FieldDefs) {
            if (f.ID === fieldId && f.Validation.toUpperCase() === FieldDefValidations.DATETIMERANGE) {
                this.isDateRangeFieldTypeCache[fieldId] = true;
                return true;
            }
        }
        this.isDateRangeFieldTypeCache[fieldId] = false;
        return false;
    }

    private isComboboxFieldTypeCache: { [id: string]: boolean } = {};
    public isComboboxFieldType(fieldId: string): boolean {
        if (!(this.isComboboxFieldTypeCache[fieldId] === undefined)) return this.isComboboxFieldTypeCache[fieldId];
        for (const f of this.FieldDefs) {
            if (f.ID === fieldId && f.Validation.toUpperCase() === FieldDefValidations.COMBOBOX) {
                this.isComboboxFieldTypeCache[fieldId] = true;
                return true;
            }
        }
        this.isComboboxFieldTypeCache[fieldId] = false;
        return false;
    }

    private isLocationListFieldTypeCache: { [id: string]: boolean } = {};
    public isLocationListFieldType(fieldId: string): boolean {
        if (!(this.isLocationListFieldTypeCache[fieldId] === undefined)) return this.isLocationListFieldTypeCache[fieldId];
        for (const f of this.FieldDefs) {
            if (f.ID === fieldId && f.Validation.toUpperCase() === FieldDefValidations.LOCATIONLIST) {
                this.isLocationListFieldTypeCache[fieldId] = true;
                return true;
            }
        }
        this.isLocationListFieldTypeCache[fieldId] = false;
        return false;
    }

    private isStaffIDListFieldTypeCache: { [id: string]: boolean } = {
        ParentOwner: true,
    };
    public isStaffIDListFieldType(fieldId: string): boolean {
        if (!(this.isStaffIDListFieldTypeCache[fieldId] === undefined)) return this.isStaffIDListFieldTypeCache[fieldId];
        for (const f of this.FieldDefs) {
            if (f.ID === fieldId && f.Validation.toUpperCase() === FieldDefValidations.STAFFIDLIST) {
                this.isStaffIDListFieldTypeCache[fieldId] = true;
                return true;
            }
        }
        this.isStaffIDListFieldTypeCache[fieldId] = false;
        return false;
    }

    private isAddressFieldTypeCache: { [id: string]: boolean } = {
        ParentLocation: true,
    };
    public isAddressFieldType(fieldId: string): boolean {
        if (!(this.isAddressFieldTypeCache[fieldId] === undefined)) return this.isAddressFieldTypeCache[fieldId];
        for (const f of this.FieldDefs) {
            if (f.ID === fieldId && f.Validation.toUpperCase() === FieldDefValidations.ADDRESS) {
                this.isAddressFieldTypeCache[fieldId] = true;
                return true;
            }
        }
        this.isAddressFieldTypeCache[fieldId] = false;
        return false;
    }

    private isCheckListFieldTypeCache: { [id: string]: boolean } = {
        ParentCategory: true,
        ParentType: true,
    };
    public isCheckListFieldType(fieldId: string): boolean {
        if (!(this.isCheckListFieldTypeCache[fieldId] === undefined)) return this.isCheckListFieldTypeCache[fieldId];
        for (const f of this.FieldDefs) {
            if (f.ID === fieldId && f.Validation.toUpperCase() === FieldDefValidations.CHECKLIST) {
                this.isCheckListFieldTypeCache[fieldId] = true;
                return true;
            }
        }
        this.isCheckListFieldTypeCache[fieldId] = false;
        return false;
    }

    private isComboBoxFieldTypeCache: { [id: string]: boolean } = {};
    public isComboBoxFieldType(fieldId: string): boolean {
        if (!(this.isComboBoxFieldTypeCache[fieldId] === undefined)) return this.isComboBoxFieldTypeCache[fieldId];
        for (const f of this.FieldDefs) {
            if (f.ID === fieldId && f.Validation.toUpperCase() === FieldDefValidations.COMBOBOX) {
                this.isComboBoxFieldTypeCache[fieldId] = true;
                return true;
            }
        }
        this.isComboBoxFieldTypeCache[fieldId] = false;
        return false;
    }

    private isDateTimeFieldTypeCache: { [id: string]: boolean } = {};
    public isDateTimeFieldType(fieldId: string): boolean {
        return this.isDateTimeFieldTypeCache[fieldId];
    }

    private isDurationFieldTypeCache: { [id: string]: boolean } = {};
    public isDurationFieldType(fieldId: string): boolean {
        if (this.isDurationFieldTypeCache[fieldId] != null) return this.isDurationFieldTypeCache[fieldId];
        for (const f of this.FieldDefs) {
            if (f.ID === fieldId && f.Validation.toUpperCase() === FieldDefValidations.DURATION) {
                this.isDurationFieldTypeCache[fieldId] = true;
                return true;
            }
        }
        this.isDurationFieldTypeCache[fieldId] = false;
        return false;
    }

    private propertyWithOperatorAndValuePattern = XRegExp("([\\p{L}\\p{Nd}_]+?)([ =><!\\s]+)('?[\\p{L}\\p{Nd}_\\-\\s]*'?)( \\(([\\p{Nd}_\\s]*)([\\p{L}\\p{Nd}]*)\\)\\s*)?", 'gu');
    private identifiersPattern = XRegExp("[\\p{L}\\p{Nd}_\\s]+?[=><! ]+'?[\\p{L}\\p{Nd}_\\-\\s]*'?(\\([\\p{Nd}]+\\s?([\\p{L}])*\\))?", 'gu');
    private timeBufferPattern = XRegExp('([\\p{L}\\p{Nd}_\\s]+?)\\(([\\p{Nd}_\\s]*)([\\p{L}]*)\\)\\s*', 'u');

    private getCriteriaMatches(criteria: string): Criteria {
        const operatorANDPattern = /(\b\s?AND\s?\b)/g;
        const operatorORPattern = /(\b\s?OR\s?\b)/g;

        criteria = criteria.replace(operatorANDPattern, ' && ');
        criteria = criteria.replace(operatorORPattern, ' || ');
        const matches = criteria.match(this.identifiersPattern);
        const bufferMap = {};
        if (matches) {
            for (const match of matches) {
                bufferMap[match] = match.match(this.timeBufferPattern);
            }
        }
        return { matches, criteria, buffer: bufferMap };
    }
    private parseCriteriaChunks(chunks: string[], criteria: CriteriaObj, buffer: RegExpMatchArray): string {
        if (chunks.length > 1) {
            const left = chunks[1].trim();
            const right = chunks[3].trim();
            let operator = chunks[2].trim();

            let compareFields = false;
            for (const objType in Record.allPropertiesCriteriaCache) {
                if (Record.allPropertiesCriteriaCache[objType][left] || ListDataMetaData[left]) {
                    criteria.hasPropForObjectType[objType] = true;
                }
                if (right && (Record.allPropertiesCriteriaCache[objType][right] || ListDataMetaData[right])) {
                    compareFields = true;
                }
            }
            const isNull: boolean = right.toString().toLowerCase() === 'null';
            const isSecurityRole = left === ST_CRITERIA_SECURITY_ROLE;
            const securityRole = this.securityPrivileges[this.nomWebServicesJson.HelloResults.HomeProgram]?.privId || null;
            let prop = '';
            if (operator === '=') operator = '==';
            if (this.isCheckListFieldType(left)) {
                // Left side is checklist field
                if (operator === '==') {
                    if (isNull) {
                        prop += `!rec.${left} || !rec.${left}.length`;
                    } else {
                        prop += `rec.${left} && rec.${left}.includes(${right})`;
                    }
                }
                if (operator === '!=') {
                    if (isNull) {
                        prop += `rec.${left} && rec.${left}.length`;
                    } else {
                        prop += `!rec.${left} || !rec.${left}.includes(${right})`;
                    }
                }
            } else {
                const possibleNumber = parseFloat(right);
                if (!isNaN(possibleNumber)) {
                    // Right side is a number
                    if (operator === '==') {
                        prop += `rec.${left}${operator}${right}`;
                    }
                    if (operator === '!=') {
                        prop += `!rec.${left} || rec.${left}${operator}${right}`;
                    }
                } else if (compareFields) {
                    // Right side is a field
                    if (this.isDateProperty(left)) {
                        prop += `((!!rec.${left}) ? rec.${left}.get() : false)`;
                    } else {
                        if (isSecurityRole) {
                            prop += `'${securityRole}'`;
                        } else {
                            prop += 'rec.' + left;
                        }
                    }
                    if (this.isDateProperty(right)) {
                        if (buffer) {
                            if (operator === '>') {
                                prop += ' - ' + `((!!rec.${right}) ? rec.${right}.get() : false)` + ` >= ${parseInt(buffer[2]) * Util.timeBufferGranularity(buffer[3])}`;
                            }
                            if (operator === '<') {
                                prop += ' - ' + `((!!rec.${right}) ? rec.${right}.get() : false)` + ` <= ${parseInt(buffer[2]) * (-1 * Util.timeBufferGranularity(buffer[3]))}`;
                            }
                        } else {
                            prop += `${operator}((!!rec.${right}) ? rec.${right}.get() : false)`;
                        }
                    } else {
                        prop += `${operator}rec.${right}`;
                    }
                } else {
                    try {
                        if (isNull) {
                            // Right side is null
                            if (this.isDateProperty(left)) {
                                if (operator === '==') {
                                    prop += `!rec.${left} || rec.${left}.isBlank()`;
                                }

                                if (operator === '!=') {
                                    prop += `!!rec.${left} && !rec.${left}.isBlank()`;
                                }
                            } else {
                                if (operator === '==') {
                                    if (isSecurityRole) {
                                        prop += `${!securityRole}`;
                                    } else {
                                        prop += '!rec.' + left;
                                    }
                                }
                                if (operator === '!=') {
                                    if (isSecurityRole) {
                                        prop += `${!!securityRole}`;
                                    } else {
                                        prop += '!!rec.' + left;
                                    }
                                }
                            }
                        } else {
                            if (this.isDateProperty(left)) {
                                prop += `(!!rec.${left}) ? rec.${left}.get()`;
                            } else {
                                if (isSecurityRole) {
                                    prop += `('${securityRole}')?('${securityRole}'`;
                                } else {
                                    prop += '(rec.' + left + ')? (rec.' + left;
                                }
                            }
                            prop += `${operator + right}) : false`;
                        }
                    } catch (error) {
                        console.log(left);
                        console.log(error);
                    }
                }
            }
            return prop;
        }
        return null;
    }

    public isPropertyForType: { [id: number]: (fieldDef: FieldDef) => boolean } = {};
    public constructCriteriaMappings() {
        Record.allPropertiesCriteriaCache = {};
        for (const objectType in this.isPropertyForType) {
            Record.allPropertiesCriteriaCache[objectType] = {};
            Record.allPropertiesCriteriaCache[objectType][PSTFieldNamesEnum.Title] = {
                ID: PSTFieldNamesEnum.Title,
            };
            Record.allPropertiesCriteriaCache[objectType][PSTFieldNamesEnum.Slug] = {
                ID: PSTFieldNamesEnum.Slug,
            };
            Record.allPropertiesCriteriaCache[objectType][PSTFieldNamesEnum.Type] = {
                ID: PSTFieldNamesEnum.Type,
            };
        }
        for (const field in this.FieldDefsDict) {
            for (const objectType in this.isPropertyForType) {
                if (this.isPropertyForType[objectType](this.FieldDefsDict[field])) {
                    Record.allPropertiesCriteriaCache[objectType][field] = this.FieldDefsDict[field];
                }
            }
        }

        const processVisibilityCriteria = (f: FieldDef | ExternalAction) => {
            if (f.VisibilityCriteria && f.VisibilityCriteria.trim().length > 0) {
                const m = this.getCriteriaMatches(f.VisibilityCriteria);
                f.VisibilityCriteria = m.criteria;
                const visibilityCriteriaObj = new CriteriaObj();
                if (m.matches) {
                    for (const match of m.matches) {
                        const chunks = match.trim().split(this.propertyWithOperatorAndValuePattern);
                        if (chunks.length > 1) {
                            const propName = chunks[1].trim();
                            const prop = this.parseCriteriaChunks(chunks, visibilityCriteriaObj, m.buffer[match]);
                            if (prop !== null) {
                                f.VisibilityCriteria = f.VisibilityCriteria.replace(match.trim(), prop);
                            }

                            if (propName === ST_CRITERIA_SECURITY_ROLE) {
                                if (!Record.visibilityCriteriaReverseCache[PSTFieldNames.PSTCreatedBy]) Record.visibilityCriteriaReverseCache[PSTFieldNames.PSTCreatedBy] = [];
                                Record.visibilityCriteriaReverseCache[PSTFieldNames.PSTCreatedBy].push(f.ID);
                            } else {
                                if (!Record.visibilityCriteriaReverseCache[propName]) Record.visibilityCriteriaReverseCache[propName] = [];
                                Record.visibilityCriteriaReverseCache[propName].push(f.ID);
                            }

                            if (chunks[3] && ListDataMetaData[chunks[3].trim()]) {
                                if (!Record.visibilityCriteriaReverseCache[chunks[3].trim()]) Record.visibilityCriteriaReverseCache[chunks[3].trim()] = [];
                                Record.visibilityCriteriaReverseCache[chunks[3].trim()].push(f.ID);
                            }
                        }
                    }
                    visibilityCriteriaObj.criteria = f.VisibilityCriteria;
                    Record.visibilityCriteriaCache[f.ID] = visibilityCriteriaObj;
                }
            }
        };

        const processReadOnlyCriteria = (f: FieldDef) => {
            //Read Only Criteria
            if (f.ReadOnlyCriteria && f.ReadOnlyCriteria.trim().length > 0) {
                const m = this.getCriteriaMatches(f.ReadOnlyCriteria);
                f.ReadOnlyCriteria = m.criteria;
                const readOnlyCriteriaObj = new CriteriaObj();
                if (m.matches) {
                    for (const match of m.matches) {
                        const chunks = match.trim().split(this.propertyWithOperatorAndValuePattern);
                        if (chunks.length > 1) {
                            const propName = chunks[1].trim();
                            const prop = this.parseCriteriaChunks(chunks, readOnlyCriteriaObj, m.buffer[match]);
                            if (prop !== null) {
                                f.ReadOnlyCriteria = f.ReadOnlyCriteria.replace(match.trim(), prop);
                            }

                            if (propName === ST_CRITERIA_SECURITY_ROLE) {
                                if (!Record.readOnlyCriteriaReverseCache[PSTFieldNames.PSTCreatedBy]) Record.readOnlyCriteriaReverseCache[PSTFieldNames.PSTCreatedBy] = [];
                                Record.readOnlyCriteriaReverseCache[PSTFieldNames.PSTCreatedBy].push(f.ID);
                            } else {
                                if (!Record.readOnlyCriteriaReverseCache[propName]) Record.readOnlyCriteriaReverseCache[propName] = [];
                                Record.readOnlyCriteriaReverseCache[propName].push(f.ID);
                            }

                            if (chunks[3] && ListDataMetaData[chunks[3].trim()]) {
                                if (!Record.readOnlyCriteriaReverseCache[chunks[3].trim()]) Record.readOnlyCriteriaReverseCache[chunks[3].trim()] = [];
                                Record.readOnlyCriteriaReverseCache[chunks[3].trim()].push(f.ID);
                            }
                        }
                    }
                    readOnlyCriteriaObj.criteria = f.ReadOnlyCriteria;
                    Record.readOnlyCriteriaCache[f.ID] = readOnlyCriteriaObj;
                }
            }
        };

        this.FieldDefs.forEach((f: FieldDef) => {
            processVisibilityCriteria(f);
            processReadOnlyCriteria(f);
        });
        this.externalActionsService.ExternalActions.forEach(processVisibilityCriteria);
    }

    public constructAdornerVisibilityCriteriaCaches(adornersMap: { [id: string]: Adorner }) {
        for (const key in adornersMap) {
            const adorner = adornersMap[key];
            if (adorner.VisibilityCriteria && adorner.AdornerLocation) {
                const m = this.getCriteriaMatches(adorner.VisibilityCriteria);
                adorner.VisibilityCriteria = m.criteria;
                const visibilityCriteriaObj = new CriteriaObj();
                if (m.matches) {
                    for (const match of m.matches) {
                        const chunks = match.trim().split(this.propertyWithOperatorAndValuePattern);
                        if (chunks.length > 1) {
                            let prop = '';
                            const propName = chunks[1].trim();
                            if (
                                (adorner.AdornerLocation === this.adornersIDS.TopicCardTypeColor && propName === PSTFieldNames.PSTTopicType) ||
                                (adorner.AdornerLocation === this.adornersIDS.StoryIcon && propName === PSTFieldNames.PSTStoryOutputType) ||
                                (adorner.AdornerLocation === this.adornersIDS.AssignmentIcon && propName === PSTFieldNames.DescriptionType)
                            ) {
                                for (const objType in Record.allPropertiesCriteriaCache) {
                                    if (Record.allPropertiesCriteriaCache[objType][propName] || ListDataMetaData[propName]) {
                                        visibilityCriteriaObj.hasPropForObjectType[objType] = true;
                                    }
                                }
                                if (chunks[2].trim() === '=') chunks[2] = '==';
                                prop += `this.itemType().toUpperCase()${chunks[2]}${chunks[3]}.toUpperCase()`;
                            } else {
                                prop = this.parseCriteriaChunks(chunks, visibilityCriteriaObj, m.buffer[match]);
                            }
                            if (prop !== null) {
                                adorner.VisibilityCriteria = adorner.VisibilityCriteria.replace(match.trim(), prop);
                            }
                        }
                    }

                    visibilityCriteriaObj.criteria = adorner.VisibilityCriteria;

                    if (!Record.adornerVisibilityCriteriaCache[adorner.AdornerLocation]) Record.adornerVisibilityCriteriaCache[adorner.AdornerLocation] = [];
                    Record.adornerVisibilityCriteriaCache[adorner.AdornerLocation].push({ visibilityCriteria: visibilityCriteriaObj, adorner });
                }
            }
        }

        for (const l in Record.adornerVisibilityCriteriaCache) {
            const vcsArr = Record.adornerVisibilityCriteriaCache[l];
            vcsArr.sort((a, b) => a.adorner.Order - b.adorner.Order);
        }
    }

    public objectDefaultValueFieldCache: { [id: number]: { [id: string]: any } } = {};
    public objectCopyFromParentFieldCache: { [id: number]: { [id: string]: any } } = {};
    private defaultValueFieldCache: { [id: string]: any } = {};

    public populateExtraFiltersCallback: any = null;

    public populateCopyFromParentCacheCallbacks: { [id: number]: (f: FieldDef) => void } = {};
    public populateCopyFromParentCache() {
        for (const f of this.FieldDefs) {
            for (const key in this.populateCopyFromParentCacheCallbacks) {
                this.populateCopyFromParentCacheCallbacks[key](f);
            }
        }
    }

    public populateDefaultValueCallbacks: { [id: number]: (f: FieldDef, criteriaObj: any) => void } = {};
    public populateDefaultValue() {
        for (const f of this.FieldDefs) {
            if (f.DefaultValue && f.DefaultValue.trim() !== '') {
                const criteriaObj = {
                    key: f.ID,
                    value: '',
                    validation: f.Validation.toUpperCase(),
                };

                if (f.Validation.toUpperCase() === FieldDefValidations.COMBOBOX) {
                    // debugger;
                    const listValues = f.List.split('|');
                    if (listValues.length > 0) {
                        if (listValues.indexOf(f.DefaultValue) > -1) {
                            criteriaObj.value = f.DefaultValue;
                        } else {
                            criteriaObj.value = '';
                        }
                    } else {
                        criteriaObj.value = '';
                    }
                } else if (f.Validation.toUpperCase() === FieldDefValidations.CHECKBOX) {
                    if (f.DefaultValue.toUpperCase() === 'TRUE' || f.DefaultValue === '1') {
                        criteriaObj.value = '1';
                    } else if (f.DefaultValue.toUpperCase() === 'FALSE' || f.DefaultValue === '0') {
                        criteriaObj.value = '0';
                    } else {
                        criteriaObj.value = '';
                    }
                } else {
                    criteriaObj.value = f.DefaultValue;
                }

                for (const key in this.populateDefaultValueCallbacks) {
                    this.populateDefaultValueCallbacks[key](f, criteriaObj);
                }
                this.defaultValueFieldCache[f.ID] = criteriaObj;
            }
        }
    }

    public getDefaultValue(fieldID: string): any {
        return this.defaultValueFieldCache[fieldID];
    }

    private securityRolesCache: { [id: string]: any } = {};

    public populateSecurityRoles(roles: any) {
        for (const role of roles) {
            this.securityRolesCache[role.ID] = role;
        }
    }

    public getSecurityRole(roleId: string): any {
        return this.securityRolesCache[roleId] ? this.securityRolesCache[roleId] : null;
    }

    private listColumnsForSpecialFieldCache: { [id: string]: Array<{ [id: string]: any }> } = {};
    public getListColumnsForSpecialField(field: string): Array<{ [id: string]: any }> {
        if (this.listColumnsForSpecialFieldCache[field]) return this.listColumnsForSpecialFieldCache[field];
        this.listColumnsForSpecialFieldCache[field] = [];
        for (const f of this.FieldDefs) {
            if (f.ID === field && (f.Validation.toUpperCase() === FieldDefValidations.SPECIAL || f.Validation.toUpperCase() === FieldDefValidations.COST)) {
                let columns = f.List ? f.List.split('|') : [];
                if (f.Validation.toUpperCase() === FieldDefValidations.COST) {
                    columns = ['CostType,C\\', 'Cost,Text,15'];
                }
                for (const col of columns) {
                    const chunks = col.split(',');
                    const colName = chunks[0];
                    let validationType = chunks[1].toUpperCase();
                    let colWidth = 100;
                    let list: { id: string; title: string }[] = [];
                    if (chunks.length > 1) {
                        if (chunks[1].indexOf('C\\') > -1) {
                            validationType = FieldDefValidations.COMBOBOX;
                        }
                        if (f.Validation.toUpperCase() === FieldDefValidations.SPECIAL) {
                            const listValues = chunks[1].split('\\');
                            list = listValues.splice(1).map(v => ({ id: v, title: this.calculateListItemTitle(field, v, v) }));
                        } else if (f.Validation.toUpperCase() === FieldDefValidations.COST) {
                            list = f.List ? f.List.split('|').map(v => ({ id: v, title: this.calculateListItemTitle(field, v, v) })) : [];
                        }
                    } else {
                        validationType = FieldDefValidations.TEXT;
                    }
                    if (chunks.length > 2) {
                        if (chunks[2].trim()) colWidth = parseInt(chunks[2].trim());
                    }

                    const columnMetadata = {
                        header: colName,
                        validation: validationType,
                        columnWidth: colWidth,
                        list,
                    };
                    this.listColumnsForSpecialFieldCache[field].push(columnMetadata);
                }
                break;
            }
        }
        return this.listColumnsForSpecialFieldCache[field];
    }

    private listColumnsForAddressFieldCache: Array<{ [id: string]: any }> = [];
    public getListColumnsForAddress(): Array<{ [id: string]: any }> {
        if (this.listColumnsForAddressFieldCache && this.listColumnsForAddressFieldCache.length > 0) return this.listColumnsForAddressFieldCache;
        this.listColumnsForAddressFieldCache = [];
        const columns =
            'Country,Text|AdministrativeArea,Text|Locality,Text|StreetAddress,Text|LocationName,Text|PostalCode,Text|Latitude,Text|Longitude,Text|LocationId,Text|FormattedAddress,Text|ShortCountry,Text|ShortAdministrativeArea,Text|Title,Text|SubLocality,Text|Neighborhood,Text|City,Text|State,Text'.split(
                '|'
            );
        for (const col of columns) {
            const chunks = col.split(',');
            const colName = chunks[0];
            let validationType = chunks[1].toUpperCase();
            let colWidth = 100;
            let list = [];
            if (chunks.length > 1) {
                if (chunks[1].indexOf('C\\') > -1) {
                    validationType = FieldDefValidations.COMBOBOX;
                }
                const listValues = chunks[1].split('\\');
                list = listValues.splice(1);
            } else {
                validationType = FieldDefValidations.TEXT;
            }
            if (chunks.length > 2) {
                if (chunks[2].trim()) colWidth = parseInt(chunks[2].trim());
            }

            const columnMetadata = {
                header: colName,
                validation: validationType,
                columnWidth: colWidth,
                list,
            };
            this.listColumnsForAddressFieldCache.push(columnMetadata);
        }
        return this.listColumnsForAddressFieldCache;
    }

    public setFieldDefCallbacks: { [id: number]: (fieldDef: FieldDef) => void } = {};
    public setFieldDefs(fieldDefsRes: any) {
        this.store.dispatch(GlobalTablesActions.setUserLocationPrivileges({ securityPrivileges: this.securityPrivileges }));
        this.FieldDefs = [];
        this.FieldDefsDict = {};
        for (let i = 0; i < fieldDefsRes.length; i++) {
            const fieldDef: FieldDef = fieldDefsRes[i];
            if (!fieldDef.FieldGroup || fieldDef.FieldGroup === 'undefined') fieldDef.FieldGroup = '';
            this.FieldDefs.push(fieldDef);
            this.FieldDefsDict[fieldDef.ID] = fieldDef;

            if ([FieldDefValidations.TIME, FieldDefValidations.DATE, FieldDefValidations.MOSDATETIME].indexOf(fieldDef.Validation.toUpperCase() as FieldDefValidations) > -1) {
                this.isDateTimeFieldTypeCache[fieldDef.ID] = true;
            }

            for (const key in this.setFieldDefCallbacks) {
                this.setFieldDefCallbacks[key](fieldDef);
            }
        }

        // for (const key in this.calculateDefaultGroupSort) {
        //     this.calculateDefaultGroupSort[key]();
        // }

        // Add live field defs
        const serversFieldDef = {} as FieldDef;
        serversFieldDef.ID = PSTFieldNames.PSTServerLocations;
        serversFieldDef.DefaultCaption = PSTFieldNames.PSTServerLocations;
        serversFieldDef.Briefable = true;
        serversFieldDef.TopicProp = false;
        serversFieldDef.AssignmentProp = false;
        serversFieldDef.RundownProp = true;
        serversFieldDef.StoryProp = true;
        serversFieldDef.DiscoverWireProp = false;
        serversFieldDef.Validation = FieldDefValidations.TREE;
        serversFieldDef.Type = 'Script+RO Property';
        serversFieldDef.FieldGroup = '';

        this.FieldDefs.push(serversFieldDef);
        this.FieldDefsDict[serversFieldDef.ID] = serversFieldDef;

        // Add discover field defs
        const publishedFieldDef = {} as FieldDef;
        publishedFieldDef.ID = PSTFieldNames.PSTWirePublished;
        publishedFieldDef.DefaultCaption = this.languageSettings.PSTWebSortByPubTimeLabel;
        publishedFieldDef.Briefable = true;
        publishedFieldDef.TopicProp = false;
        publishedFieldDef.AssignmentProp = false;
        publishedFieldDef.DiscoverWireProp = true;
        publishedFieldDef.Validation = FieldDefValidations.DATE;
        publishedFieldDef.GroupableProp = true;
        publishedFieldDef.SortableProp = true;
        this.FieldDefs.push(publishedFieldDef);
        this.FieldDefsDict[publishedFieldDef.ID] = publishedFieldDef;

        const providerFieldDef = {} as FieldDef;
        providerFieldDef.ID = PSTFieldNames.PSTWireProvider;
        providerFieldDef.DefaultCaption = this.languageSettings.PSTWebGroupByWireProviderLabel;
        providerFieldDef.Briefable = true;
        providerFieldDef.TopicProp = false;
        providerFieldDef.AssignmentProp = false;
        providerFieldDef.DiscoverWireProp = true;
        providerFieldDef.Validation = FieldDefValidations.SELECT;
        this.FieldDefs.push(providerFieldDef);
        this.FieldDefsDict[providerFieldDef.ID] = providerFieldDef;

        const wireCategoryFieldDef = {} as FieldDef;
        wireCategoryFieldDef.ID = PSTFieldNames.PSTWireCategory;
        wireCategoryFieldDef.DefaultCaption = this.languageSettings.PSTWebGroupByWireCategoryLabel;
        wireCategoryFieldDef.Briefable = true;
        wireCategoryFieldDef.TopicProp = false;
        wireCategoryFieldDef.AssignmentProp = false;
        wireCategoryFieldDef.DiscoverWireProp = true;
        wireCategoryFieldDef.Validation = FieldDefValidations.SELECT;
        this.FieldDefs.push(wireCategoryFieldDef);
        this.FieldDefsDict[wireCategoryFieldDef.ID] = wireCategoryFieldDef;

        const wireType = {} as FieldDef;
        wireType.ID = PSTFieldNames.PSTWireType;
        wireType.DefaultCaption = this.languageSettings.PSTWebGroupByWireTypeLabel;
        wireType.Briefable = false;
        wireType.TopicProp = false;
        wireType.AssignmentProp = false;
        wireType.DiscoverWireProp = true;
        wireType.StoryProp = false;
        wireType.RundownProp = false;
        wireType.GroupableProp = true;
        wireType.SortableProp = true;
        wireType.Validation = FieldDefValidations.SELECT;
        this.FieldDefs.push(wireType);
        this.FieldDefsDict[wireType.ID] = wireType;

        const roModTime = {} as FieldDef;
        roModTime.ID = PSTFieldNames.PSTROModTime;
        roModTime.DefaultCaption = 'Rundown Start time';
        roModTime.Type = 'RO Property';
        roModTime.Briefable = true;
        roModTime.TopicProp = false;
        roModTime.AssignmentProp = false;
        roModTime.DiscoverWireProp = false;
        roModTime.StoryProp = false;
        roModTime.RundownProp = true;
        roModTime.Validation = FieldDefValidations.DATE;
        this.FieldDefs.push(roModTime);
        this.FieldDefsDict[roModTime.ID] = roModTime;

        this.setWireType();

        this.store.dispatch(GlobalTablesActions.processGlobalFields({ fields: this.FieldDefsDict }));
        this.fieldDefsLoadedSource.next(true);
    }

    private setWireType() {
        this.idToDescriptionMap['WIRE_TYPE_0'] = this.languageSettings.PSTWebDiscoverWireTypeRSS;
        this.idToDescriptionMap['WIRE_TYPE_1'] = this.languageSettings.PSTWebDiscoverWireTypeAP;
        this.idToDescriptionMap['WIRE_TYPE_2'] = this.languageSettings.PSTWebDiscoverWireTypeTwitter;
        this.idToDescriptionMap['WIRE_TYPE_4'] = this.languageSettings.PSTWebDiscoverWireTypeFTP;
    }

    public isValidationTypeImplemented(type: string) {
        return this.ImplementedValidationTypes.indexOf(type.toUpperCase()) > -1;
    }

    public Staff: { [id: string]: Staff } = {};

    public securityPrivileges: { [id: string]: GroupSecurityPrivs } = {};

    private listItemsForDeskCache: { [id: number]: Array<{ id: string; title: string; selectable: boolean; initials?: string }> } = {};
    public idToDescriptionMap: { [id: string]: string } = {};
    public getListItemsForDesk(type: number): Array<{ id: string; title: string; selectable: boolean; initials?: string }> {
        if (this.listItemsForDeskCache[type]?.length > 0) return this.listItemsForDeskCache[type];
        if (!this.listItemsForDeskCache[type]) this.listItemsForDeskCache[type] = [];
        if (this.FieldDefs.length > 0) {
            const f = this.FieldDefsDict[PSTFieldNames.PSTDesk];
            const chunks = f.List ? f.List.split('|') : [];
            for (const c of chunks) {
                this.idToDescriptionMap[c] = c;
                const title = this.calculateListItemTitle(f.ID, c, c);
                if (!this.securityPrivileges[c]) {
                    if (this.securityPrivileges[this.nomWebServicesJson.PSTGroup]) {
                        this.listItemsForDeskCache[type].push({
                            id: c,
                            title,
                            selectable: this.calculateEditableForDesk(this.nomWebServicesJson.PSTGroup, type),
                            initials: this.calculateInitials(title),
                        });
                    }
                } else if (this.securityPrivileges[c]) {
                    this.listItemsForDeskCache[type].push({
                        id: c,
                        title,
                        selectable: this.calculateEditableForDesk(c, type),
                        initials: this.calculateInitials(title),
                    });
                }
            }
            for (const key in this.Groups) {
                const group = this.Groups[key];
                if (group.PlaybookGroup) {
                    this.idToDescriptionMap[group.ID] = group.Description;

                    const title = this.calculateListItemTitle(group.ID, group.Description, group.Description);
                    if (this.securityPrivileges[group.ID]) {
                        if (this.securityPrivileges[group.ID]) {
                            this.listItemsForDeskCache[type].push({
                                id: group.ID,
                                title,
                                selectable: this.calculateEditableForDesk(group.ID, type),
                                initials: this.calculateInitials(title),
                            });
                        }
                    } else if (this.securityPrivileges[this.nomWebServicesJson.PSTGroup]) {
                        this.listItemsForDeskCache[type].push({
                            id: group.ID,
                            title,
                            selectable: this.calculateEditableForDesk(this.nomWebServicesJson.PSTGroup, type),
                            initials: this.calculateInitials(title),
                        });
                    }
                }
            }
            return this.listItemsForDeskCache[type];
        }
        return [];
    }

    public getListItemsForWireProvider(): Array<{ id: string; title: string; selectable: boolean }> {
        return this.WireProviders.map(provider => {
            return {
                id: provider.providerId,
                title: provider.name,
                selectable: true,
            };
        });
    }

    public getListItemsForWireCategory(): Array<{ id: string; title: string; selectable: boolean }> {
        return this.WireCategories.map(category => {
            return {
                id: category.id,
                title: category.description,
                selectable: true,
            };
        });
    }

    public getListItemsForTeamLocations(): Array<any> {
        return this.PlaybookTeamsArray.filter(t => t.PlaybookTeam).map(team => ({
            id: team.ID,
            title: team.Description,
            selectable: true,
        }));
    }

    public clearDeskListItemCache() {
        this.listItemsForDeskCache = [];
        this.idToDescriptionMap = {};
    }

    public async getGlobalTablesSimple(): Promise<ServiceResponse<void>> {
        const response: HttpResponse<any> = await this.nomWebServicesJson.get('/api/GlobalTables', {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
            }),
            responseType: 'json',
            params: {
                // filterLangPrefix: 'PSTWeb',
                filterLangPrefix: 'NONE',
                filterInActiveUsers: 'True',
            },
            observe: 'response',
        });
        const res = response.body;
        this.languageSettings.populate(res._LanguageSettings);
        this.store.dispatch(i18nActions.initializeI18n({ data: res._LanguageSettings }));
        this.clearTitleCache();

        this.Apps = res._Apps;

        this.visualStateService.ConfigSettings = {};
        for (const configSetting of res._ConfigSettings) {
            this.visualStateService.ConfigSettings[configSetting.FieldName] = configSetting.FieldValue;
        }

        if (!this.visualStateService.ConfigSettings.MaxRows || this.visualStateService.ConfigSettings.MaxRows > 10000) {
            this.visualStateService.ConfigSettings.MaxRows = 10000;
        }
        this.setIsGroupSingleValue();
        this.setPlaybookHelpUrl();
        this.setEnforceRequiredFieldsOnCreate();

        this.Groups = {};
        this.AllGroups = {};
        this.PlaybookTeamsArray = [];
        this.GroupsArray = [];
        for (const group of res._Groups) {
            this.AllGroups[group.ID] = group;
            this.AllGroupsArray.push(this.AllGroups[group.ID]);
            if (group.PlaybookGroup || group.PlaybookTeam || group.PSTGroup) {
                this.Groups[group.ID] = group;
                this.GroupsArray.push(this.Groups[group.ID]);

                if (group.PSTGroup === true) {
                    this.nomWebServicesJson.PSTGroup = group.ID;
                    this.store.dispatch(GlobalTablesActions.setPstGroupId({ PSTGroupId: group.ID }));
                    this.nomWebServicesJson.PSTServer = group.Server;
                    this.PlaybookTeamsArray.push(this.Groups[group.ID]);
                    this.DefaultPlaybookTeam = this.Groups[group.ID];
                    this.EnterprisePlaybookGroup = group.ID;
                    this.nomWebServicesJson.currentPlaybookTeam = this.DefaultPlaybookTeam.ID;
                } else if (group.PlaybookTeam === true) {
                    this.PlaybookTeamsArray.push(this.Groups[group.ID]);
                }
            }
        }
        if (!this.nomWebServicesJson.PSTGroup || !this.nomWebServicesJson.PSTServer) {
            this.nomWebServicesJson.PSTServer = this.nomWebServicesJson.HelloResults.HomeDatabase;
        }
        this.HomeServerGroups = this.AllGroupsArray.filter(group => group.Server === this.nomWebServicesJson.HelloResults.HomeDatabase).map(group => group.ID);

        this.Languages = [];
        for (const language of res._AvailableLanguages) {
            const languageId = language.BaseLanguage + language.Dialect;
            const languageInfo: ENPSLanguage = { id: languageId, title: language.FriendlyName };
            if (languageId === this.nomWebServicesJson.HelloResults.PersonalLanguage) {
                this.PersonalLanguage = languageInfo;
            }
            this.Languages.push(languageInfo);
        }
        if (this.nomWebServicesJson.HelloResults.PersonalLanguage) {
            moment.locale(this.nomWebServicesJson.HelloResults.PersonalLanguage.toLowerCase());
        }

        this.MOSDevices = res._MOSDevices;
        this.Privileges = res._Privileges;

        // TODO: add all the fields at some point
        this.store.dispatch(GlobalTablesActions.setGlobalTables({ MOSDevices: this.MOSDevices, Privileges: this.Privileges }));

        this.externalActionsService.ExternalActions = [];
        for (const action of res._ExternalActions) {
            this.externalActionsService.ExternalActions.push(action);
            Record.allActionsCriteriaCache[action.ID] = action;
        }

        this.FieldMappings = [];
        if (res._FieldMapping) {
            this.FieldMappings = res._FieldMapping;
        }

        this.Servers = {};
        for (const server of res._Servers) {
            this.Servers[server.ServerID] = server;
        }

        this.Staff = {};
        for (const staff of res._Staff) {
            this.Staff[staff.UserID] = new Staff(
                staff.UserID,
                staff.GivenName,
                staff.GroupDescription,
                staff.Program,
                staff.ServerDescription,
                staff.ServerID,
                staff.Supervisor,
                staff.Surname,
                staff.Email,
                staff.Title,
                staff.Phone,
                `${staff.GivenName} ${staff.Surname}`,
                this.calculateInitials(`${staff.GivenName} ${staff.Surname}`)
            );
        }

        this.StorytellingNavigation = [];
        this.StorytellingNavigationDict = {};
        for (const storytellingNavigation of res._StorytellingNavigation) {
            this.StorytellingNavigation.push(storytellingNavigation);
            this.StorytellingNavigationDict[storytellingNavigation.Product] = storytellingNavigation;
        }
        this.StorytellingNavigation.sort((a, b) => (a.Sequence > b.Sequence ? 1 : b.Sequence > a.Sequence ? -1 : 0));

        this.CustomFilterDef = [];
        // ?dd All and Blank by default
        const allCustomFilterDef: CustomFilterDef = new CustomFilterDef('ALL', this.languageSettings.PSTWebAllOption, 'Date', 'all');
        const blankCustomFilterDef: CustomFilterDef = new CustomFilterDef('BLANK', this.languageSettings.PSTWebBlankOption, 'Date', 'blank');
        this.CustomFilterDef.push(allCustomFilterDef);
        this.CustomFilterDefsDict.ALL = allCustomFilterDef;
        this.CustomFilterDef.push(blankCustomFilterDef);
        this.CustomFilterDefsDict.BLANK = blankCustomFilterDef;

        for (const filter of res._CustomFilters) {
            const customFilter: CustomFilterDef = new CustomFilterDef(filter.ID, filter.DefaultCaption, filter.Validation, filter.FilterValue);
            this.CustomFilterDef.push(customFilter);
            this.CustomFilterDefsDict[filter.ID] = customFilter;
        }

        this.store.dispatch(GlobalTablesActions.setCustomFilterDefs({ customFilterDefs: this.CustomFilterDef, customFilterDefsDict: this.CustomFilterDefsDict }));

        this.CommonTimeZones = [];
        this.IanaTimeZones = [];

        const getTimezoneLabel = (tzId: string, title: string) => {
            const timeZoneLabel = 'PSTWebTimeZone';
            if (this.languageSettings.get(`${timeZoneLabel}_${tzId}`).includes(timeZoneLabel)) {
                return title;
            }
            return this.languageSettings.get(`${timeZoneLabel}_${tzId}`);
        };
        if (res._CommonTimeZones) {
            //            res._CommonTimeZones.forEach(tz => {
            //                this.CommonTimeZones.push(new TimeZone(tz.TZID, getTimezoneLabel(tz.TZID, tz.DisplayName)))
            //            });
            moment.tz.names().forEach(currentValue => {
                let isExisting: boolean = false;
                res._CommonTimeZones.forEach(tz => {
                    if (tz.TZID === currentValue) {
                        isExisting = true;
                    }
                });
                if (!isExisting) {
                    // Now show TimeZones even with offset that is not a whole number of hours
                    this.IanaTimeZones.push({ id: currentValue, title: getTimezoneLabel(currentValue, currentValue) });
                }
            });
            const allTimeZones: Array<TimeZone> = [...this.CommonTimeZones, ...this.IanaTimeZones];
            this.DefaultTimeZone = { id: '', title: '' };
            let clientTimeZoneId = '';
            try {
                clientTimeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch (err) {
                clientTimeZoneId = 'America/New_York';
            }

            const clientTimeZone = allTimeZones.filter(t => t.id === clientTimeZoneId);
            if (clientTimeZone && clientTimeZone.length > 0) {
                this.ClientTimeZone = { id: clientTimeZone[0].id, title: getTimezoneLabel(clientTimeZone[0].id, clientTimeZone[0].title) };
            } else {
                const defaultTimeZone = allTimeZones.filter(t => t.id === 'America/New_York');
                if (defaultTimeZone && defaultTimeZone.length > 0) {
                    this.ClientTimeZone = { id: defaultTimeZone[0].id, title: getTimezoneLabel(defaultTimeZone[0].id, defaultTimeZone[0].title) };
                } else {
                    this.ClientTimeZone = { id: 'America/New_York', title: getTimezoneLabel('America/New_York', 'America/New_York') };
                }
            }

            if (this.nomWebServicesJson.HelloResults && this.nomWebServicesJson.HelloResults.PersonalTimeZone) {
                const personalTimeZone = allTimeZones.filter(t => t.id === this.nomWebServicesJson.HelloResults.PersonalTimeZone);
                if (personalTimeZone && personalTimeZone.length > 0) {
                    this.PersonalTimeZone = { id: personalTimeZone[0].id, title: getTimezoneLabel(personalTimeZone[0].id, personalTimeZone[0].title) };
                    this.PersonalTimeZoneDisplay = this.PersonalTimeZone;
                } else {
                    this.PersonalTimeZone = { id: this.ClientTimeZone.id, title: getTimezoneLabel(this.ClientTimeZone.id, this.ClientTimeZone.title) };
                    this.PersonalTimeZoneDisplay = this.PersonalTimeZone;
                }
            } else {
                this.PersonalTimeZone = { id: this.ClientTimeZone.id, title: getTimezoneLabel(this.ClientTimeZone.id, this.ClientTimeZone.title) };
                this.PersonalTimeZoneDisplay = this.PersonalTimeZone;
            }

            moment.tz.setDefault(this.PersonalTimeZone?.id || null);

            this.store.dispatch(
                GlobalTablesActions.setTimeZoneSettings({
                    personalTimeZone: this.PersonalTimeZone,
                    personalTimeZoneDisplay: this.PersonalTimeZoneDisplay,
                    clientTimeZone: this.ClientTimeZone,
                    defaultTimeZone: this.DefaultTimeZone,
                })
            );

            if (this.nomWebServicesJson.HelloResults && this.nomWebServicesJson.HelloResults.PersonalLanguage) {
                const personalLanguage = this.Languages.filter(t => t.id === this.nomWebServicesJson.HelloResults.PersonalLanguage);
                if (personalLanguage && personalLanguage.length > 0) {
                    this.PersonalLanguage = { id: personalLanguage[0].id, title: personalLanguage[0].title };
                } else {
                    this.PersonalLanguage = null;
                }
            } else {
                this.PersonalLanguage = null;
            }

            if (this.nomWebServicesJson.HelloResults && this.nomWebServicesJson.HelloResults.DayOfWeek) {
                this.store.dispatch(HelloResultsActions.setWeekDaySetting({ dayOfWeek: this.nomWebServicesJson.HelloResults.DayOfWeek }));
            }
            this.store.dispatch(HelloResultsActions.setLanguageSettings({ language: this.PersonalLanguage?.id ? this.PersonalLanguage.id : '' }));
        }

        this.Servers = {};
        for (const server of res._Servers) {
            this.Servers[server.ServerID] = server;
        }

        if (this.nomWebServicesJson.HelloResults.SecurityPrivileges) {
            for (const priv of this.nomWebServicesJson.HelloResults.SecurityPrivileges) {
                this.securityPrivileges[priv.GroupId] = this.calculatePrivs(priv);
            }

            if (!this.securityPrivileges[this.nomWebServicesJson.PSTGroup]) {
                this.securityPrivileges[this.nomWebServicesJson.PSTGroup] = new GroupSecurityPrivs();
            }
        }

        this.Adorners = {};
        for (const adorner of res._Adorners) {
            this.Adorners[adorner.ID] = adorner;
        }

        await this.getWireProviders();
        await this.getWireCategories();
        const ret = await this.getGlobalFields();

        this.constructAdornerVisibilityCriteriaCaches(this.Adorners);
        this.nomWebServicesJson.sendServiceData();
        return ret;
    }

    public async getWireProviders(): Promise<ServiceResponse<void>> {
        try {
            const response: HttpResponse<unknown> = await this.nomWebServicesJson.discoverAPIGet(this.nomWebServicesJson.DiscoverAPIBaseUrl + '/providers/me', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    Authorization: this.nomWebServicesJson.token,
                    accept: 'text/plain',
                }),
                responseType: 'json',
                observe: 'response',
            });
            this.WireProviders = <Array<DiscoverProviderResponse>>response.body;

            return new ServiceResponse<void>(true);
        } catch (e) {
            console.log(e);
        }
        return new ServiceResponse<void>(false);
    }

    public async getWireCategories(): Promise<ServiceResponse<void>> {
        try {
            const response: HttpResponse<unknown> = await this.nomWebServicesJson.discoverAPIGet(this.nomWebServicesJson.DiscoverAPIBaseUrl + '/wire-categories', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    Authorization: this.nomWebServicesJson.token,
                    accept: 'text/plain',
                }),
                responseType: 'json',
                observe: 'response',
            });
            this.WireCategories = <Array<DiscoverWireCategoryResponse>>response.body;

            return new ServiceResponse<void>(true);
        } catch (e) {
            console.log(e);
        }
        return new ServiceResponse<void>(false);
    }

    public async getGlobalFields(): Promise<ServiceResponse<void>> {
        const response: HttpResponse<unknown> = await this.nomWebServicesJson.get('/api/GlobalFields', {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
            }),
            responseType: 'json',
            params: {
                // filterLangPrefix: 'PSTWeb',
                filterLangPrefix: 'NONE',
                filterInActiveUsers: 'True',
            },
            observe: 'response',
        });
        const res = response.body;
        this.setFieldDefs(res);
        this.constructCriteriaMappings();
        this.populateDefaultValue();
        this.populateCopyFromParentCache();
        return new ServiceResponse<void>(true);
    }

    protected calculatePrivs(priv: any): GroupSecurityPrivs {
        const res = new GroupSecurityPrivs();
        if (!this.nomWebServicesJson.HelloResults.Supervisor) {
            if (priv.TopicPrivileges !== GroupPermissions.READWRITE) {
                res.topic.write = false;
            }
            if (priv.TopicPrivileges === GroupPermissions.NONE) {
                res.topic.read = false;
            }

            if (priv.StoryPrivileges !== GroupPermissions.READWRITE) {
                res.story.write = false;
            }
            if (priv.StoryPrivileges === GroupPermissions.NONE) {
                res.story.read = false;
            }

            if (priv.AssignmentPrivileges !== GroupPermissions.READWRITE) {
                res.assignment.write = false;
            }
            if (priv.AssignmentPrivileges === GroupPermissions.NONE) {
                res.assignment.read = false;
            }

            if (priv.CostPrivileges !== GroupCostPermissions.READWRITE) {
                res.cost.write = false;
            }
            if (priv.CostPrivileges === GroupCostPermissions.NONE) {
                res.cost.read = false;
            }
            if (!priv.ViewUserLocationPrivileges) {
                res.viewUserLocationPrivileges = false;
            }
        }
        res.privId = priv.PrivId;
        return res;
    }

    public getRtl(): Direction {
        return this.nomWebServicesJson?.HelloResults?.Rtl ? 'rtl' : 'ltr';
    }

    public getUserNameByIDV2(userID: string): string {
        if (!userID) return '';

        return this.Staff[userID] ? `${this.Staff[userID].GivenName} ${this.Staff[userID].Surname}` : userID;
    }

    getDisplayOpenActionButtonHoverTitle(inSidecarOpenedMode: boolean): Signal<string> {
        if (inSidecarOpenedMode) {
            return this.store
                .selectSignal(selectTranslatedKey(`${PSTWEB_BUTTON_HT}_${i18nKeys.PSTWebOpenText}`))()
                .includes(PSTWEB_BUTTON_HT)
                ? this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebOpenText))
                : this.store.selectSignal(selectTranslatedKey(`${PSTWEB_BUTTON_HT}_${i18nKeys.PSTWebOpenText}`));
        } else {
            return this.store
                .selectSignal(selectTranslatedKey(`${PSTWEB_BUTTON_HT}_${i18nKeys.PSTWebOpenFullScreenText}`))()
                .includes(PSTWEB_BUTTON_HT)
                ? this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebOpenFullScreenText))
                : this.store.selectSignal(selectTranslatedKey(`${PSTWEB_BUTTON_HT}_${i18nKeys.PSTWebOpenFullScreenText}`));
        }
    }
}
